/**
 * Mobile "Add to Bag" bottom sheet (product pages, mobile only).
 *
 * The on-page variant picker stays the single source of truth: option pills
 * rendered in the sheet drive the picker's inputs, and the theme's standard
 * `on:variant:change` event keeps the sheet in sync.
 */
if (!customElements.get('mobile-bottom-bag')) {
  class MobileBottomBag extends HTMLElement {
    constructor() {
      super();

      this.onOpen = this.onOpen.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('.section-main-product');
      if (!this.section) return;

      this.trigger = this.querySelector('.js-mbb-trigger');
      this.triggerLabel = this.querySelector('.js-mbb-trigger-label');
      this.triggerPrice = this.querySelector('.js-mbb-trigger-price');
      this.overlay = this.querySelector('.js-mbb-overlay');
      this.sheet = this.querySelector('.js-mbb-sheet');
      this.dragZone = this.querySelector('.js-mbb-drag');
      this.closeBtn = this.querySelector('.js-mbb-close');
      this.viewSelect = this.querySelector('.js-mbb-view-select');
      this.viewAdded = this.querySelector('.js-mbb-view-added');
      this.headingSelect = this.querySelector('.js-mbb-heading-select');
      this.headingAdded = this.querySelector('.js-mbb-heading-added');
      this.optionsEl = this.querySelector('.js-mbb-options');
      this.productMedia = this.querySelector('.js-mbb-product-media');
      this.priceEl = this.querySelector('.js-mbb-price');
      this.errorEl = this.querySelector('.js-mbb-error');
      this.submitBtn = this.querySelector('.js-mbb-submit');
      this.checkoutLink = this.querySelector('.js-mbb-checkout');
      this.continueBtn = this.querySelector('.js-mbb-continue');
      this.addedMedia = this.querySelector('.js-mbb-added-media');
      this.addedTitle = this.querySelector('.js-mbb-added-title');
      this.addedPrice = this.querySelector('.js-mbb-added-price');
      this.addedOptions = this.querySelector('.js-mbb-added-options');
      this.addedQty = this.querySelector('.js-mbb-added-qty');

      this.picker = this.section.querySelector('variant-picker');
      this.variantData = this.getPickerData();

      this.variantId = this.dataset.initialVariantId || null;
      this.variantAvailable = this.dataset.initialAvailable === 'true';
      this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';
      this.productAvailable = !this.trigger.disabled;
      this.dragStartY = 0;
      this.dragOffset = 0;
      this.isDragging = false;

      this.trigger.addEventListener('click', this.onOpen);
      this.overlay.addEventListener('click', this.onClose);
      this.closeBtn.addEventListener('click', this.onClose);
      this.continueBtn.addEventListener('click', this.onClose);
      this.submitBtn.addEventListener('click', this.onSubmit);
      this.section.addEventListener('on:variant:change', this.onVariantChange);
      this.dragZone.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.dragZone.addEventListener('touchmove', this.onTouchMove, { passive: true });
      this.dragZone.addEventListener('touchend', this.onTouchEnd);
      this.dragZone.addEventListener('touchcancel', this.onTouchEnd);

      // Reserve space below the page content so the fixed trigger does not
      // cover the end of the product description / related content.
      document.body.classList.add('has-mobile-bottom-bag');

      // Hide the fixed trigger while the on-page Add to Bag button is visible.
      this.initPageAtcWatcher();

      this.syncOptions();
      this.updateSubmitState();
    }

    /**
     * Watches the on-page Add to Bag button: while it is within the viewport
     * the fixed bottom trigger is hidden, and it reappears once the button
     * scrolls out of view.
     */
    initPageAtcWatcher() {
      const pageAtc = this.section.querySelector('.product-form button.add-to-cart, .product-form button[name="add"]');
      if (!pageAtc || typeof IntersectionObserver === 'undefined') return;

      this.pageAtcObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle('mobile-bottom-bag--atc-visible', entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );
      this.pageAtcObserver.observe(pageAtc);
    }

    disconnectedCallback() {
      document.body.classList.remove('has-mobile-bottom-bag');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.section) this.section.removeEventListener('on:variant:change', this.onVariantChange);
      if (this.pageAtcObserver) this.pageAtcObserver.disconnect();
      if (this.isOpen()) this.unlockScroll();
    }

    isOpen() {
      return this.classList.contains('mobile-bottom-bag--open');
    }

    getPickerData() {
      if (!this.picker) return null;
      try {
        const dataEl = this.picker.querySelector('[type="application/json"]');
        return dataEl ? JSON.parse(dataEl.textContent) : null;
      } catch (error) {
        return null;
      }
    }

    getOptionNames() {
      try {
        const names = JSON.parse(this.dataset.optionNames || '[]');
        return Array.isArray(names) ? names : [];
      } catch (error) {
        return [];
      }
    }

    /**
     * Returns a theme-formatted price string for a variant id, if available.
     */
    formattedPrice(variantId) {
      if (!variantId || !this.variantData || !this.variantData.formatted) return null;
      const entry = this.variantData.formatted[variantId];
      return entry ? entry.price : null;
    }

    /**
     * Builds option pill rows from the on-page variant picker.
     */
    buildOptions() {
      this.optionsEl.textContent = '';
      if (!this.picker || this.hasOnlyDefaultVariant) return;

      const selectors = Array.from(this.picker.querySelectorAll('.option-selector'));

      // Only the "size" option is offered in the sheet. Other options keep
      // their current on-page selection. Falls back to showing every option
      // when the product has no size-named option.
      const sizeIndexes = selectors
        .map((selector, index) => ({ index, name: (selector.dataset.option || '').trim() }))
        .filter((entry) => /size/i.test(entry.name))
        .map((entry) => entry.index);
      const visibleIndexes = sizeIndexes.length ? sizeIndexes : selectors.map((_, index) => index);

      selectors.forEach((selector, optionIndex) => {
        if (!visibleIndexes.includes(optionIndex)) return;

        const row = document.createElement('div');
        row.className = 'mobile-bottom-bag__option';

        // Use the option name rendered by Liquid (data-option) instead of the
        // DOM label text, which can pick up extra content such as the size
        // chart link rendered next to the legend.
        // const labelText = (selector.dataset.option || '').trim();
        // if (labelText) {
        //   const label = document.createElement('div');
        //   label.className = 'mobile-bottom-bag__option-label';
        //   label.textContent = labelText;
        //   row.appendChild(label);
        // }

        const pills = document.createElement('div');
        pills.className = 'mobile-bottom-bag__pills';
        const isDropdown = selector.dataset.selectorType === 'dropdown';
        const sources = isDropdown
          ? selector.querySelectorAll('.custom-select .js-option')
          : selector.querySelectorAll('input.js-option');

        sources.forEach((source) => {
          const value = isDropdown ? (source.dataset.value || '') : source.value;
          if (value === '') return; // Skip placeholder entries

          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'mobile-bottom-bag__pill js-mbb-pill';
          pill.dataset.optionIndex = String(optionIndex);
          pill.dataset.value = value;

          if (isDropdown) {
            pill.textContent = source.textContent.trim();
          } else {
            const sourceLabel = source.id ? selector.querySelector(`label[for="${source.id}"] .js-value`) : null;
            pill.textContent = sourceLabel ? sourceLabel.textContent.trim() : value;
          }

          pill.addEventListener('click', () => this.selectOption(source, isDropdown));
          pills.appendChild(pill);
        });

        row.appendChild(pills);
        this.optionsEl.appendChild(row);
      });

      this.syncOptions();
    }

    /**
     * Mirrors the on-page picker state (selected / availability) onto the pills.
     */
    syncOptions() {
      if (!this.picker) return;

      const selectors = this.picker.querySelectorAll('.option-selector');
      this.querySelectorAll('.js-mbb-pill').forEach((pill) => {
        const selector = selectors[Number(pill.dataset.optionIndex)];
        if (!selector) return;

        const isDropdown = selector.dataset.selectorType === 'dropdown';
        let source = null;

        if (isDropdown) {
          source = Array.from(selector.querySelectorAll('.custom-select .js-option'))
            .find((option) => (option.dataset.value || '') === pill.dataset.value);
          pill.classList.toggle('is-selected', !!source && source.getAttribute('aria-selected') === 'true');
        } else {
          source = Array.from(selector.querySelectorAll('input.js-option'))
            .find((option) => option.value === pill.dataset.value);
          pill.classList.toggle('is-selected', !!source && source.checked);
        }

        pill.classList.toggle('is-unavailable', !!source && source.classList.contains('is-unavailable'));
        pill.setAttribute('aria-pressed', pill.classList.contains('is-selected') ? 'true' : 'false');
      });
    }

    /**
     * Applies a pill selection on the on-page variant picker, which fires the
     * standard theme machinery (variant resolution, price, media, URL updates).
     */
    selectOption(source, isDropdown) {
      if (isDropdown) {
        if (source.getAttribute('aria-selected') === 'true') return;
      } else if (source.checked) {
        return;
      }
      source.click();
    }

    updateTrigger() {
      if (!this.productAvailable) return;

      this.triggerLabel.textContent = this.dataset.labelAdd || '';

      let priceText = null;
      if (this.variantId) {
        priceText = this.formattedPrice(this.variantId);
      } else if (this.variantData && Array.isArray(this.variantData.variants) && this.variantData.variants.length) {
        const cheapest = this.variantData.variants.reduce(
          (min, variant) => (variant.price < min.price ? variant : min)
        );
        priceText = `${this.dataset.fromText || ''} ${this.formattedPrice(cheapest.id) || ''}`.trim();
      }

      if (priceText) {
        this.triggerPrice.textContent = ` - ${priceText}`;
        this.triggerPrice.hidden = false;
      } else {
        this.triggerPrice.hidden = true;
      }
    }

    updateSheetPrice() {
      if (!this.priceEl || !this.productAvailable) return;

      let priceText = null;
      if (this.variantId) {
        priceText = this.formattedPrice(this.variantId);
      } else if (this.variantData && Array.isArray(this.variantData.variants) && this.variantData.variants.length) {
        const cheapest = this.variantData.variants.reduce(
          (min, variant) => (variant.price < min.price ? variant : min)
        );
        priceText = `${this.dataset.fromText || ''} ${this.formattedPrice(cheapest.id) || ''}`.trim();
      }

      if (priceText) this.priceEl.textContent = priceText;
    }

    updateSubmitState() {
      this.submitBtn.disabled = !(this.variantId && this.variantAvailable);
    }

    onVariantChange(evt) {
      const variant = evt.detail ? evt.detail.variant : null;
      this.variantId = variant ? String(variant.id) : null;
      this.variantAvailable = !!(variant && variant.available);

      this.updateTrigger();
      this.syncOptions();
      this.updateSubmitState();
      this.updateSheetPrice();
    }

    onOpen() {
      if (this.isOpen() || this.trigger.disabled) return;

      this.buildOptions();
      this.updateSubmitState();
      this.resetError();
      this.showSelectView();
      this.updateSheetMedia();
      this.updateSheetPrice();

      this.sheet.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
      this.trigger.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => {
        this.classList.add('mobile-bottom-bag--open');
        this.closeBtn.focus({ preventScroll: true });
      });
    }

    onClose() {
      if (!this.isOpen()) return;

      this.classList.remove('mobile-bottom-bag--open');
      this.sheet.style.transform = '';
      document.removeEventListener('keydown', this.onKeydown);
      this.trigger.setAttribute('aria-expanded', 'false');

      const hideSheet = () => {
        if (this.isOpen()) return;
        this.sheet.hidden = true;
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        hideSheet();
      } else {
        setTimeout(hideSheet, 300);
      }

      this.unlockScroll();
      this.trigger.focus({ preventScroll: true });
    }

    unlockScroll() {
      document.body.style.overflow = '';
    }

    showSelectView() {
      this.viewSelect.hidden = false;
      this.viewAdded.hidden = true;
      this.headingSelect.hidden = false;
      this.headingAdded.hidden = true;
      this.submitBtn.hidden = false;
      this.checkoutLink.hidden = true;
      this.continueBtn.hidden = true;
    }

    showAddedView() {
      this.viewSelect.hidden = true;
      this.viewAdded.hidden = false;
      this.headingSelect.hidden = true;
      this.headingAdded.hidden = false;
      this.submitBtn.hidden = true;
      this.checkoutLink.hidden = false;
      this.continueBtn.hidden = false;
    }

    updateSheetMedia() {
      if (!this.productMedia) return;
      const activeImg = this.section.querySelector(
        '.media-gallery .main-image .slider__item.is-active .product-media img'
      );
      if (!activeImg) return;
      const clone = activeImg.cloneNode(false);
      clone.setAttribute('loading', 'lazy');
      clone.setAttribute('alt', '');
      this.productMedia.replaceChildren(clone);
    }

    onKeydown(evt) {
      if (evt.key === 'Escape') {
        evt.preventDefault();
        this.onClose();
        return;
      }
      if (evt.key === 'Tab') this.trapFocus(evt);
    }

    trapFocus(evt) {
      const focusables = this.sheet.querySelectorAll(
        'button:not([hidden]):not([disabled]), a[href]:not([hidden])'
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    onTouchStart(evt) {
      if (!this.isOpen()) return;
      this.dragStartY = evt.touches[0].clientY;
      this.isDragging = true;
      this.sheet.classList.add('is-dragging');
    }

    onTouchMove(evt) {
      if (!this.isDragging) return;
      const delta = evt.touches[0].clientY - this.dragStartY;
      this.dragOffset = Math.max(0, delta);
      this.sheet.style.transform = `translateY(${this.dragOffset}px)`;
    }

    onTouchEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.sheet.classList.remove('is-dragging');
      if (this.dragOffset > 90) {
        this.onClose();
      } else {
        this.sheet.style.transform = '';
      }
      this.dragOffset = 0;
    }

    resetError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    getAddErrorMessage(data) {
      if (!data) return this.dataset.errorGeneric;
      if (typeof data.description === 'string' && data.description) return data.description;
      if (typeof data.message === 'string' && data.message) return data.message;
      if (data.errors) {
        if (typeof data.errors === 'string') return data.errors;
        try {
          return Object.values(data.errors).flat().join(', ');
        } catch (error) {
          // fall through to generic message
        }
      }
      return this.dataset.errorGeneric;
    }

    onSubmit() {
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      this.resetError();
      if (!this.variantId) {
        this.showError(this.dataset.errorSelect);
        return;
      }
      if (!this.variantAvailable) {
        this.showError(theme.strings.noStock);
        return;
      }

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((sel) => document.querySelector(sel))
        .filter((el) => el && el.dataset.sectionId);

      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', '1');
      if (sectionsToUpdate.length) {
        formData.append('sections', sectionsToUpdate.map((el) => el.dataset.sectionId).join(','));
      }
      formData.append('sections_url', window.location.pathname);

      fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) throw new Error(this.getAddErrorMessage(data));

          if (sectionsToUpdate.length) {
            sectionsToUpdate.forEach((el) => {
              if (typeof el.updateFromCartChange === 'function') {
                el.updateFromCartChange(data.sections[el.dataset.sectionId]);
              }
            });
          } else {
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          }

          this.showAdded(data);
        })
        .catch((error) => {
          this.showError(error && error.message ? error.message : this.dataset.errorGeneric);
        })
        .finally(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.removeAttribute('aria-disabled');
          this.updateSubmitState();
        });
    }

    sizedImage(src, width) {
      if (!src || src.indexOf('cdn.shopify.com') === -1) return src;
      const separator = src.indexOf('?') === -1 ? '?' : '&';
      return `${src}${separator}width=${width}`;
    }

    /**
     * Renders the added-item summary from the /cart/add.js response and keeps
     * the sheet open.
     */
    showAdded(data) {
      const img = document.createElement('img');
      if (data.image) {
        img.src = this.sizedImage(data.image, 120);
      } else {
        const sheetImg = this.productMedia ? this.productMedia.querySelector('img') : null;
        if (sheetImg && sheetImg.src) img.src = sheetImg.src;
      }
      img.alt = '';
      img.loading = 'lazy';
      this.addedMedia.replaceChildren(img);

      this.addedTitle.textContent = data.product_title || '';
      const priceText = this.formattedPrice(data.variant_id ? String(data.variant_id) : this.variantId);
      // Fallback to the displayed price (e.g. default-variant products with no picker data).
      this.addedPrice.textContent = priceText || (this.priceEl ? this.priceEl.textContent : '');

      this.addedOptions.textContent = '';
      const optionNames = this.getOptionNames();
      const optionValues = Array.isArray(data.variant_options) ? data.variant_options : [];
      optionValues.forEach((value, index) => {
        if (!value || value === 'Default Title') return;
        const line = document.createElement('div');
        const name = optionNames[index];
        line.textContent = name ? `${name}: ${value}` : value;
        this.addedOptions.appendChild(line);
      });

      this.addedQty.textContent = data.quantity || 1;

      this.resetError();
      this.showAddedView();
      this.checkoutLink.focus({ preventScroll: true });
    }
  }

  customElements.define('mobile-bottom-bag', MobileBottomBag);
}
